import {
  describeFailure,
  type GraphCollectionResponse,
  type GraphListResult,
  isHttpRequestError,
  providerCodeOf,
} from '@ns/plugin-msgraph-proxy-client';
import {
  describeSourceMismatch,
  toSubmitValidator,
} from '@ns/plugin-picker-shared';

import {
  buildGroupOptions,
  buildServicePrincipalOptions,
  GROUP_FIELDS,
  isNonBlankType,
  SERVICE_PRINCIPAL_FIELDS,
  type ServicePrincipalOption,
} from './directory-options';
import {
  buildGroupLookupQuery,
  buildServicePrincipalLookupQuery,
  isGraphObjectId,
} from './directory-query';
import {
  ENTRA_GROUP_PICKER,
  ENTRA_SERVICE_PRINCIPAL_PICKER,
} from './field-names';
import { PERMANENT_REFUSAL_CODES } from './query-state';
import { readGraphTransport } from './transport';
import type { EntraGroup, EntraServicePrincipal } from './types';
import {
  buildGroupsQueryOptions,
  buildServicePrincipalsQueryOptions,
  graphQueryClient,
} from './use-directory';

/** Graph's provider code for an id that names no directory object. */
const RESOURCE_NOT_FOUND_CODE = 'Request_ResourceNotFound';

/**
 * Whether Graph answered a lookup by id that the id names no directory object.
 *
 * @remarks
 * Only a 404 whose provider body carries `Request_ResourceNotFound` counts. A
 * 404 without that code, or a refusal written by the proxy, stays a failed query.
 */
function isResourceNotFound(error: unknown): boolean {
  return (
    isHttpRequestError(error) &&
    error.status === 404 &&
    providerCodeOf(error) === RESOURCE_NOT_FOUND_CODE
  );
}

/**
 * Runs a lookup by id and returns Graph's answer, the collection response.
 *
 * @remarks
 * Graph's 404 with `Request_ResourceNotFound` becomes an answer without rows.
 * Every other failure is rethrown, for the validator to classify.
 */
async function lookUpResponse<TRow>(
  lookUp: () => Promise<GraphListResult<TRow>>,
): Promise<GraphCollectionResponse<TRow>> {
  try {
    const { response } = await lookUp();
    return response;
  } catch (error) {
    if (isResourceNotFound(error)) return { value: [] };
    throw error;
  }
}

/**
 * Whether a restored value is exactly what the picker writes: every key of the
 * record as a string, no other key, with a non-blank `id` and `type`.
 *
 * @remarks
 * The Entra value is closed, so a value with a key more or less is not one the
 * picker wrote.
 */
function isSelection<T>(
  data: unknown,
  fields: Readonly<Record<keyof T, true>>,
): data is T {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    return false;
  }
  const value = new Map<string, unknown>(Object.entries(data));
  const keys = Object.keys(fields);
  const id = value.get('id');
  return (
    value.size === keys.length &&
    keys.every(key => typeof value.get(key) === 'string') &&
    typeof id === 'string' &&
    id.trim() !== '' &&
    isNonBlankType(value.get('type'))
  );
}

/** The group fields compared exactly with the offered row. The row is found by `id`, and the display name is not compared. */
const GROUP_SOURCE_FIELDS = [
  'type',
] as const satisfies readonly (keyof EntraGroup)[];

/** The service-principal fields compared exactly with the offered row. The row is found by `id`, and the display name is not compared. */
const SERVICE_PRINCIPAL_SOURCE_FIELDS = [
  'type',
  'appId',
] as const satisfies readonly (keyof EntraServicePrincipal)[];

/**
 * Validates that a selected Entra group is still offered, with the same `type`.
 *
 * @remarks
 * It looks the group up at Graph by its exact `id`, so the answer is complete:
 * a row the picker cannot offer, or Graph's 404 for an id that names no object,
 * means the group is no longer offered. An `id` that is no Graph object id is
 * blocked before any request, because the picker writes the id Graph returned.
 */
export const groupValidator = toSubmitValidator<EntraGroup, undefined>(
  async (value, context) => {
    if (
      !isSelection<EntraGroup>(value, GROUP_FIELDS) ||
      !isGraphObjectId(value.id)
    ) {
      return 'The selected group does not match what the picker writes. Choose it again.';
    }
    const transport = readGraphTransport(context.apiHolder);
    const response = await lookUpResponse(() =>
      graphQueryClient.query(
        buildGroupsQueryOptions(buildGroupLookupQuery(value.id), transport),
      ),
    );
    return describeSourceMismatch<EntraGroup, EntraGroup>({
      isCaseInsensitive: false,
      isComplete: true,
      items: buildGroupOptions(response).options,
      sourceFields: GROUP_SOURCE_FIELDS,
      subject: 'group',
      submittedValue: value,
    });
  },
  {
    describeFailure,
    fieldName: ENTRA_GROUP_PICKER,
    logPrefix: 'entra-pickers groupValidator',
    permanentRefusalCodes: PERMANENT_REFUSAL_CODES,
    subject: 'group',
  },
);

/**
 * Validates that a selected Entra service principal is still offered, with the same `type` and `appId`.
 *
 * @remarks
 * It looks the service principal up at Graph by its exact `id`, so the answer
 * is complete: a row the picker cannot offer, such as one without an `appId`,
 * or Graph's 404 for an id that names no object, means the service principal
 * is no longer offered. An `id` that is no Graph object id is blocked before
 * any request, because the picker writes the id Graph returned.
 */
export const servicePrincipalValidator = toSubmitValidator<
  EntraServicePrincipal,
  undefined
>(
  async (value, context) => {
    if (
      !isSelection<EntraServicePrincipal>(value, SERVICE_PRINCIPAL_FIELDS) ||
      !isGraphObjectId(value.id)
    ) {
      return 'The selected service principal does not match what the picker writes. Choose it again.';
    }
    const transport = readGraphTransport(context.apiHolder);
    const response = await lookUpResponse(() =>
      graphQueryClient.query(
        buildServicePrincipalsQueryOptions(
          buildServicePrincipalLookupQuery(value.id),
          transport,
        ),
      ),
    );
    return describeSourceMismatch<
      EntraServicePrincipal,
      ServicePrincipalOption
    >({
      isCaseInsensitive: false,
      isComplete: true,
      items: buildServicePrincipalOptions(response).options,
      sourceFields: SERVICE_PRINCIPAL_SOURCE_FIELDS,
      subject: 'service principal',
      submittedValue: value,
    });
  },
  {
    describeFailure,
    fieldName: ENTRA_SERVICE_PRINCIPAL_PICKER,
    logPrefix: 'entra-pickers servicePrincipalValidator',
    permanentRefusalCodes: PERMANENT_REFUSAL_CODES,
    subject: 'service principal',
  },
);
